import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createStory } from "../utils/api";
import { createInteractiveStory } from "../utils/apis/interactiveStoriesAPI";
import {
  Box,
  Button,
  TextField,
  Typography,
  CardMedia,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Grid2,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PlaceholderImage from "@mui/icons-material/Image";
import { t } from "@lingui/macro";

const StoryForm = ({ initialType = "text" }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [cover, setCover] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const categories = ["Romantik", "Fantastik", "Macera", "Dram", "Gençlik", "Bilim Kurgu", "Smut"];

  // 2) Add state & helpers (place with other useState lines)
  const [type, setType] = useState(initialType);
  const handleTypeChange = (_, next) => { if (next) setType(next); };

  const INTERACTIVE_GRADIENT =
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #ff8400ff 30%, #dc17c5ff 60%, #7700ffff 90%)"
      : "linear-gradient(135deg, #ff8400ff 30%, #dc17c5ff 60%, #7700ffff 90%)";

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];

      // Ensure it's an image
      if (!file.type.startsWith("image/")) {
        console.warn("Dropped file is not an image.");
        return;
      }

      handleCoverChange({ target: { files: [file] } });

      // Clear files from dataTransfer to prevent issues on macOS
      e.dataTransfer.clearData();
    } else {
      console.warn("No files detected in drop event.");
    }
  };

  const handleCategoryChange = (event, newCategories) => {
    if (newCategories.length > 2) {
      newCategories = newCategories.slice(-2);
    }
    setSelectedCategories(newCategories);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!title.trim() || !description.trim()) {
      setErrorMessage(t`Title and description cannot be empty.`);
      return;
    }

    if (title.trim().length < 1 || title.trim().length > 40) {
      setErrorMessage(t`Title must be between 1 and 40 characters.`);
      return;
    }

    if (description.trim().length < 100 || description.trim().length > 400) {
      setErrorMessage(t`Description must be between 100 and 400 characters.`);
      return;
    }

    if (selectedCategories.length === 0) {
      setErrorMessage(t`Please select at least one category.`);
      return;
    }

    setLoading(true);
    try {
      const createFn = type === "interactive" ? createInteractiveStory : createStory;
      const storyID = await createFn({
        title: title.trim(),
        description: description.trim(),
        cover,
        categories: selectedCategories,
      });
      navigate(`/mystories/${storyID}`);
    } catch (error) {
      setErrorMessage(error.message || t`Failed to create story.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      cropAndResizeImage(file, 354, 500)
        .then((resizedFile) => {
          setCover(resizedFile);
          setCoverPreview(URL.createObjectURL(resizedFile));
        })
        .catch((err) => {
          console.error("Image processing failed:", err);
        });
    }
  };

  useEffect(() => {
    return () => {
      if (coverPreview) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [coverPreview]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxWidth: 800,
        margin: "auto",
        padding: theme.spacing(3),
      }}
    >
      <Typography variant="h4" gutterBottom textAlign="center">
        {t`Create a New Story`}
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ marginBottom: theme.spacing(2) }}>
          {errorMessage}
        </Alert>
      )}

      <Grid2 container spacing={2}>
        {/* Cover Image Upload */}
        <Grid2 item xs={12} sm={4}>
          <Box
            sx={{
              height: 200,
              width: 140,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: theme.palette.grey[200],
              borderRadius: "4px",
              boxShadow: theme.shadows[2],
              overflow: "hidden",
              cursor: "pointer",
              border: `2px dashed ${isDragging ? "gold" : theme.palette.grey[400]}`,
              transition: "border-color 0.2s ease-in-out",
            }}
            onClick={() => document.getElementById("cover-upload-input").click()}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {coverPreview ? (
              <CardMedia
                component="img"
                image={coverPreview}
                alt={t`Cover Preview`}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.palette.grey[500],
                }}
              >
                <PlaceholderImage sx={{ fontSize: 64 }} />
                <Typography variant="body2" align="center" color="textSecondary">
                  {t`Drag and Drop or Click to Upload`}
                </Typography>
              </Box>
            )}
          </Box>

          <input
            id="cover-upload-input"
            type="file"
            accept="image/*"
            onChange={handleCoverChange}
            hidden
          />
        </Grid2>
        <Grid2
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",         // ← stick to top of row
            mt: 0,                            // optional: pull up a bit if you need tighter spacing
            mb: 1,
          }}
        >
          <ToggleButtonGroup
            value={type}
            exclusive
            onChange={handleTypeChange}
            size="small"
            sx={{
              borderRadius: "8px",
              overflow: "hidden",
              height: 32,                                   // enforce fixed height on group
              border: `2px solid ${theme.palette.callToAction.main}`,
              "& .MuiToggleButton-root": {
                height: "100%",                             // button fills group height
                minWidth: 80,
                border: "none",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                px: 1.5,
                display: "flex",                            // ensure flexbox
                alignItems: "center",                       // vertical centering
                justifyContent: "center",                   // horizontal centering
                lineHeight: 1.2,                            // reset default MUI line-height
                padding: 0,                                 // kill top/bottom padding
              },
            }}
          >

            <ToggleButton
              value="text"
              sx={{
                borderRadius: 0,
                opacity: 0.7,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.callToAction.main,
                  color: theme.palette.callToAction.contrastText,
                  opacity: 1,
                  "&:hover": { backgroundColor: theme.palette.callToAction.dark },
                },
              }}
            >
              {t`Text`}
            </ToggleButton>
            <ToggleButton
              value="interactive"
              sx={{
                borderRadius: 0,
                color: "transparent",
                backgroundImage: `${INTERACTIVE_GRADIENT}`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                opacity: 0.7, // faded look when not selected
                "&.Mui-selected": {
                  background: INTERACTIVE_GRADIENT,
                  color: "#fff", // plain text when selected
                  opacity: 1,
                  WebkitBackgroundClip: "unset",
                  WebkitTextFillColor: "unset",
                  "&:hover": { filter: "brightness(1.05)" },
                },
              }}
            >
              {t`Interactive`}
            </ToggleButton>

          </ToggleButtonGroup>
        </Grid2>


        {/* Story Details */}
        <Grid2 item xs={12} sm={8}>
          <TextField
            label={t`Story Title`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            inputProps={{ maxLength: 40 }}
            helperText={`${title.length}/40`}
            error={title.length < 1}
            sx={{ marginBottom: theme.spacing(2) }}
          />

          <TextField
            label={t`Description`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            fullWidth
            multiline
            rows={4}
            inputProps={{ maxLength: 400 }}
            helperText={t`${description.length}/400 (min 100 required)`}

            error={description.length < 100}
            sx={{ marginBottom: theme.spacing(2) }}
          />

          {/* Categories Selection */}
          <Typography variant="body1" sx={{ mt: 2, marginBottom: theme.spacing(2) }}>
            {t`Select Categories (1-2):`}
          </Typography>
          <ToggleButtonGroup
            value={selectedCategories}
            onChange={handleCategoryChange}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: theme.spacing(1),
              justifyContent: "center",
            }}
          >
            {categories.map((category) => (
              <ToggleButton
                key={category}
                value={category}
                sx={{
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  minWidth: "80px",
                  height: "40px",
                  transition: "all 0.2s ease-in-out",
                  backgroundColor: selectedCategories.includes(category)
                    ? theme.palette.callToAction.main
                    : theme.palette.background.paper,
                  color: selectedCategories.includes(category)
                    ? theme.palette.callToAction.contrastText
                    : theme.palette.text.primary,
                  border: `2px solid ${theme.palette.callToAction.main}`,
                  borderRadius: "8px !important",
                  "&:hover": {
                    backgroundColor: selectedCategories.includes(category)
                      ? theme.palette.callToAction.dark
                      : theme.palette.action.hover,
                    borderColor: theme.palette.callToAction.dark,
                  },
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.callToAction.main,
                    color: theme.palette.callToAction.contrastText,
                    border: `2px solid ${theme.palette.callToAction.dark} !important`,
                    "&:hover": {
                      backgroundColor: theme.palette.callToAction.dark,
                    },
                  },
                  "&:not(:first-of-type)": {
                    borderLeft: `2px solid ${theme.palette.callToAction.main} !important`,
                    marginLeft: "-2px !important",
                  },
                  "&:first-of-type": {
                    borderTopLeftRadius: "8px !important",
                    borderBottomLeftRadius: "8px !important",
                  },
                  "&:last-of-type": {
                    borderTopRightRadius: "8px !important",
                    borderBottomRightRadius: "8px !important",
                  },
                }}
              >
                {category}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {selectedCategories.includes("Smut") && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ mt: theme.spacing(1), textAlign: "center" }}
            >
              {t`Stories in category "Smut" might not be approperiate for all users.`}
            </Typography>
          )}

        </Grid2>
      </Grid2>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={
          loading ||
          selectedCategories.length === 0 ||
          description.length < 100 ||
          title.length < 1
        }
        startIcon={loading && <CircularProgress size={20} color="inherit" />}
        sx={{ marginTop: theme.spacing(3), marginhorizontal: theme.spacing(6) }}
      >
        {loading ? t`Submitting...` : t`Confirm`}
      </Button>
    </Box>
  );
};

export const cropAndResizeImage = (file, targetWidth = 354, targetHeight = 500) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (event) {
      const img = new Image();
      img.src = event.target.result;

      img.onload = function () {
        let width = img.width;
        let height = img.height;

        const inputAspectRatio = width / height;
        const targetAspectRatio = targetWidth / targetHeight;

        let cropX = 0,
          cropY = 0,
          cropWidth = width,
          cropHeight = height;

        if (inputAspectRatio > targetAspectRatio) {
          cropWidth = Math.round(height * targetAspectRatio);
          cropX = Math.round((width - cropWidth) / 2);
        } else if (inputAspectRatio < targetAspectRatio) {
          cropHeight = Math.round(width / targetAspectRatio);
          cropY = Math.round((height - cropHeight) / 2);
        }

        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(
          img,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          targetWidth,
          targetHeight
        );

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, { type: file.type });
              resolve(resizedFile);
            } else {
              reject(new Error("Image cropping failed"));
            }
          },
          file.type,
          0.8
        );
      };

      img.onerror = () => reject(new Error("Invalid image file"));
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

export default StoryForm;
